import { AutoSaveInterface, ParagraphText } from './interface/auto-save-interface';
import { EditorUploadDelegate } from './interface/editor-upload-delegate';

/**
 * 输入状态枚举
 */
export enum InputState {
    INPUT_IDLE,  // 获取焦点后，未输入过
    INPUT_START, // 开始输入
    INPUTTING, // 持续输入
    INPUT_END, // 结束输入，重置状态
}

const INPUT_TIME_INTERVAL = 2500;   // 从输入态到结束态的时间间隔
/**
 * editor编辑时，处理数据自动保存逻辑
 */
export class EditorAutoSave implements AutoSaveInterface {
    /**
     * 数据上传回调
     */
    private uploadDelegate: EditorUploadDelegate;

    /**
     * 当前输入状态
     */
    private curState: InputState = InputState.INPUT_IDLE;

    /**
     * 计时器
     */
    private inputTimer;

    /**
     * 输入前存储的段落文本数据
     */
    private paraTextsBeforeStart: ParagraphText[];

    /**
     * 输入中实时存储的段落文本数据
     */
    private paraTextsInputting: ParagraphText[];

    public constructor(uploadDelegate: EditorUploadDelegate) {
        this.uploadDelegate = uploadDelegate;
    }

    public enterInputIdle(paraTexts: ParagraphText[]) {
        this.curState = InputState.INPUT_IDLE;
        this.paraTextsBeforeStart = paraTexts;
        this.cancelTimer();
    }

    public cancel() {
        if (this.isInputState()) {
            this.cancelTimer();
            this.onInputEnd(this.paraTextsInputting, false);
        }
    }

    /**
     * 通过输入进行状态变更
     * @param paraTexts
     */
    public onEditorChange(paraTexts: ParagraphText[]) {
        if (this.isNoInputState() && !this.isParaTextEqual(paraTexts)) {
            this.onInputStart(paraTexts);
        }
        if (this.isInputState()) {
            this.onInputting(paraTexts);
        }
    }

    /**
     * 情况文字缓存
     */
    public clear() {
        this.paraTextsInputting = [];
        this.paraTextsBeforeStart = [];
    }

    private isNoInputState() {
        return this.curState === InputState.INPUT_IDLE || this.curState === InputState.INPUT_END;
    }

    private isInputState() {
        return this.curState === InputState.INPUT_START || this.curState === InputState.INPUTTING;
    }

    private cancelTimer() {
        this.inputTimer && clearTimeout(this.inputTimer);
        this.inputTimer = null;
    }

    private startTimer(paraTexts: ParagraphText[]) {
        this.inputTimer = setTimeout(() => {
            this.inputTimer = null;
            this.onInputEnd(paraTexts, true);
        }, INPUT_TIME_INTERVAL);
    }

    /**
     * 转为开始输入状态。并开启timer，去触发输入结束态
     * @param paraTexts
     */
    private onInputStart(paraTexts: ParagraphText[]) {
        this.curState = InputState.INPUT_START;
        this.doWhenInput(paraTexts);
    }

    /**
     * 从开始输入转入输入中
     * @param paraTexts
     */
    private onInputting(paraTexts: ParagraphText[]) {
        this.curState = InputState.INPUTTING;
        this.doWhenInput(paraTexts);
    }

    /**
     * 监测一段时间没用输入，则进入输入结束状态，进行数据上传
     * @param paraTexts
     * @param needUpload
     */
    private onInputEnd(paraTexts: ParagraphText[], needUpload = true) {
        this.curState = InputState.INPUT_END;
        if (this.needUploadData(needUpload, paraTexts)) {
            window.log?.info('editor auto save happen');
            this.uploadDelegate?.triggerUploadData();
        } else {
            // 如果不需要保存数据，那么重置下 hasValueChange 标记
            this.uploadDelegate?.resetHasValueChangeFlag?.();
        }
        this.paraTextsBeforeStart = paraTexts;
        this.paraTextsInputting = [];
    }

    /**
     * 判断是否触发数据上传。当文本为空或者和起始文本相同时，不上传
     * @param needUpload
     * @param paraTexts
     */
    private needUploadData(needUpload, paraTexts: ParagraphText[]) {
        return needUpload && !this.isParaTextEqual(paraTexts);
    }

    private doWhenInput(paraTexts: ParagraphText[]) {
        this.paraTextsInputting = paraTexts;
        this.cancelTimer();
        this.startTimer(paraTexts);
    }

    /**
     * 比较传入的段落文本和当前保存的是否相等
     * @param paraTexts
     */
    private isParaTextEqual(paraTexts: ParagraphText[]): boolean {
        const paraTextsOld = this.paraTextsBeforeStart;
        if (Array.isArray(paraTexts) && Array.isArray(paraTextsOld)) {
            const len = paraTexts.length;
            const lenOld = paraTextsOld.length;
            if (len !== lenOld) return false;
            for (let i = 0; i < len; i++) {
                if (paraTexts[i].paraIndex !== paraTextsOld[i].paraIndex
                    || paraTexts[i].text !== paraTextsOld[i].text) {
                    return false;
                }
            }
            return true;
        }
        return false;
    }
}
